<template>
  <div>
    <global-confirm-box ref="confirmBox" />

    <page-header
      :title="$t('restaurants_list')"
      :subtitle="$t('manage_restaurants_shops')"
      icon="mdi-silverware-fork-knife"
      icon-color="primary"
    >
      <template #actions v-if="!device.isMobile">
        <primary-button @click="showEditDialog = true">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_restaurant") }}
        </primary-button>
      </template>
    </page-header>

    <!-- Desktop View -->
    <template v-if="!device.isMobile">
      <page-toolbar :searchable="false">
        <template #filters>
          <v-row no-gutters align="center">
            <v-col cols="auto">
              <autocomplete
                v-if="isAdmin"
                v-model="selectedEntityType"
                :items="entityFilterOptions"
                item-value="val"
                :label="$t('filter_restaurant_shop')"
                color="white"
                style="width: 300px"
              />
            </v-col>
            <v-col cols="auto" class="ml-4">
              <span class="caption font-weight-medium d-block">{{ $t("status") }}</span>
              <v-btn-toggle
                v-model="statusFilter"
                mandatory
                dense
                color="primary"
                class="white status-filter-toggle"
              >
                <v-btn height="40px" value="all" color="white">{{ $t("all") }}</v-btn>
                <v-btn height="40px" value="active" color="white">{{
                  $t("active")
                }}</v-btn>
                <v-btn height="40px" value="inactive" color="white">{{
                  $t("inactive")
                }}</v-btn>
              </v-btn-toggle>
            </v-col>
            <v-col cols="auto" class="ml-4">
              <text-field
                v-model="search"
                :placeholder="$t('search')"
                kind="search"
                clearable
                :label="$t('search')"
                color="white"
                hide-details
              />
            </v-col>
          </v-row>
        </template>
      </page-toolbar>

      <div class="px-6 mt-2">
        <v-data-table
          :headers="tableHeaders"
          :items="customSearch(filteredRestaurants, tableHeaders, search)"
          class="striped-table ordera-box-shadow"
          mobile-breakpoint="0"
          :loading="isLoading"
          :loading-text="$t('loading_data')"
          show-expand
        >
          <!-- Expand button -->
          <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
            <circle-button
              v-if="item.children?.length"
              @click="expand(!isExpanded)"
              :icon="isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
            />
          </template>

          <!-- Expanded items (children) -->
          <template v-slot:expanded-item="{ item }">
            <td v-if="item.children" :colspan="tableHeaders.length" class="px-0">
              <v-data-table
                dense
                :headers="tableHeaders"
                :items="item.children"
                class="ordera-box-shadow striped-table"
                hide-default-footer
                disable-pagination
                hide-default-header
              >
                <template v-if="canManageRestaurants" v-slot:item.restoran="{ item }">
                  <v-checkbox
                    v-model="item.parent_menu"
                    @change="updateParentMenu(item)"
                  />
                </template>
                <template v-if="canManageRestaurants" v-slot:item.settings="{ item }">
                  <div class="text-right">
                    <v-checkbox
                      v-model="item.add_meal"
                      @change="updateMealSettings(item)"
                    />
                  </div>
                </template>
                <template v-slot:item.actions="{ item }">
                  <restaurant-actions
                    :item="item"
                    @edit="editRestaurant"
                    @delete="deleteRestaurant"
                    @openServicesDialog="openServicesDialog"
                    @openContractsDialog="openContractsDialog"
                  />
                </template>
              </v-data-table>
            </td>
          </template>

          <!-- Name column with location button -->
          <template v-slot:item.name="{ item }">
            <circle-button
              icon="mdi-map-marker"
              @click="openLocationDialog(item)"
              class="mr-2"
            />
            {{ toLatin(item.name) }}
            <v-chip
              v-if="!item.state"
              x-small
              color="grey lighten-1"
              text-color="white"
              class="ml-2"
            >
              {{ $t("inactive") }}
            </v-chip>
          </template>

          <!-- Empty templates for restaurant management columns -->
          <template v-if="canManageRestaurants" v-slot:item.restoran />
          <template v-if="canManageRestaurants" v-slot:item.settings />

          <template v-slot:item.city.name="{ item }">
            {{ toLatin(item.city?.name || "") }}
          </template>

          <!-- Actions column -->
          <template v-if="!isDeliveryUser" v-slot:item.actions="{ item }">
            <div class="d-flex justify-end align-center">
              <restaurant-actions
                :item="item"
                @edit="editRestaurant"
                @delete="deleteRestaurant"
                @openLinkDialog="openLinkDialog(item)"
                @openServicesDialog="openServicesDialog"
                @openContractsDialog="openContractsDialog"
              />
            </div>
          </template>
        </v-data-table>
      </div>
    </template>

    <!-- Mobile View -->
    <template v-else>
      <div class="px-3 mt-4">
        <mobile-data-cards
          :headers="mobileHeaders"
          :items="filteredRestaurants"
          :loading="isLoading"
          item-key="id"
          show-filters
          :show-row-number="false"
          :fields-per-row="2"
          :bold-fields="['name']"
        >
          <template v-slot:item.name="{ item }">
            <div class="d-flex align-center">
              <v-icon
                small
                class="mr-1"
                color="primary"
                @click="openLocationDialog(item)"
              >
                mdi-map-marker
              </v-icon>
              {{ toLatin(item.name) }}
              <v-chip
                v-if="!item.state"
                x-small
                color="grey lighten-1"
                text-color="white"
                class="ml-2"
              >
                {{ $t("inactive") }}
              </v-chip>
            </div>
          </template>
          <template v-slot:item.city.name="{ item }">
            {{ toLatin(item.city?.name || "") }}
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="d-flex align-center">
              <restaurant-actions
                :item="item"
                @edit="editRestaurant"
                @delete="deleteRestaurant"
                @openLinkDialog="openLinkDialog(item)"
                @openServicesDialog="openServicesDialog"
                @openContractsDialog="openContractsDialog"
              />
            </div>
          </template>
        </mobile-data-cards>
      </div>

      <div class="fixed-bottom-button">
        <v-btn color="primary" block @click="showEditDialog = true">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_restaurant") }}
        </v-btn>
      </div>
    </template>

    <!-- Dialogs -->
    <restaurant-edit-dialog
      v-model="showEditDialog"
      :edited-item="editedRestaurant"
      :user-restorans="accessibleRestaurants"
      :saving="isSaving"
      @close="closeEditDialog"
      @save="saveRestaurant"
    />

    <restaurant-location-dialog
      v-model="showLocationDialog"
      :restaurant="selectedRestaurantForLocation"
    />

    <restaurant-services-dialog
      v-model="showServicesDialog"
      :restaurant="selectedRestaurantForServices"
    />

    <restaurant-contracts-dialog
      v-model="showContractsDialog"
      :restaurant="selectedRestaurantForContracts"
    />

    <supplier-restaurant-link-dialog
      v-if="isAdmin"
      v-model="linkDialog"
      :fixed-restaurant="linkRestaurant"
    />
  </div>
</template>

<script>
import RestaurantEditDialog from "@/components/restaurants/RestaurantEditDialog.vue";
import RestaurantLocationDialog from "@/components/restaurants/RestaurantLocationDialog.vue";
import RestaurantServicesDialog from "@/components/restaurants/RestaurantServicesDialog.vue";
import RestaurantContractsDialog from "@/components/contracts/RestaurantContractsDialog.vue";
import RestaurantActions from "@/components/restaurants/RestaurantActions.vue";
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import { defaultRestaurant } from "@/models/restaurant";
import restaurantService from "@/services/restaurant";
import hasSnackbar from "@/mixins/hasSnackbar";
import SupplierRestaurantLinkDialog from "@/components/suppliers/SupplierRestaurantLinkDialog.vue";

export default {
  components: {
    RestaurantEditDialog,
    RestaurantLocationDialog,
    RestaurantServicesDialog,
    RestaurantContractsDialog,
    RestaurantActions,
    PageHeader,
    PageToolbar,
    MobileDataCards,
    SupplierRestaurantLinkDialog,
  },

  mixins: [hasSnackbar],

  data: () => ({
    showEditDialog: false,
    selectedEntityType: "",
    statusFilter: "all",
    allRestaurants: [],
    editedRestaurantId: null,
    editedRestaurant: { ...defaultRestaurant },
    selectedRestaurantForLocation: {},
    showLocationDialog: false,
    selectedRestaurantForServices: {},
    showServicesDialog: false,
    selectedRestaurantForContracts: {},
    showContractsDialog: false,
    isLoading: false,
    isSaving: false,
    search: "",
    linkDialog: false,
    linkRestaurant: null,
  }),

  computed: {
    currentUser() {
      return this.$store.state.user.user;
    },
    isAdmin() {
      return this.currentUser.type === "admin";
    },
    isDeliveryUser() {
      return ["dostava", "admin_delivery"].includes(this.currentUser.type);
    },
    canManageRestaurants() {
      return ["restaurant", "admin"].includes(this.currentUser.type);
    },

    entityFilterOptions() {
      return [
        { text: this.$t("all"), val: 0 },
        { text: this.$t("restaurant"), val: "restaurant" },
        { text: this.$t("shop"), val: "shop" },
      ];
    },

    baseTableHeaders() {
      return [
        { text: "ID", value: "id", sortable: true },
        { text: this.$t("title"), value: "name" },
        { text: this.$t("city"), value: "city.name", sortable: false },
        { text: this.$t("phone"), value: "phone", sortable: false },
        { text: this.$t("email"), value: "email", sortable: false },
      ];
    },

    managementHeaders() {
      return this.canManageRestaurants
        ? [
            { text: this.$t("parent_m"), value: "restoran", sortable: false },
            { text: "+ " + this.$t("add_meals"), value: "settings", sortable: false },
          ]
        : [];
    },

    actionHeaders() {
      return [
        { text: this.$t("actions"), value: "actions", sortable: false, align: "end" },
        { text: "", value: "data-table-expand" },
      ];
    },

    tableHeaders() {
      return [...this.baseTableHeaders, ...this.managementHeaders, ...this.actionHeaders];
    },

    filteredRestaurants() {
      let filtered = this.accessibleRestaurants;

      if (this.selectedEntityType && this.selectedEntityType !== 0) {
        filtered = filtered.filter(
          (item) => item.entity_type === this.selectedEntityType
        );
      }

      if (this.statusFilter === "active") {
        filtered = filtered.filter((item) => !!item.state);
      } else if (this.statusFilter === "inactive") {
        filtered = filtered.filter((item) => !item.state);
      }

      return filtered;
    },

    currentUserRole() {
      return (
        this.currentUser.restaurants
          ?.flatMap((r) => r.users)
          .find((u) => u.id === this.currentUser.id) || null
      );
    },

    accessibleRestaurants() {
      let filtered = [];
      if (this.isDeliveryUser) {
        filtered = this.allRestaurants;
      } else {
        filtered = this.filterUserAccessibleRestaurants(this.allRestaurants);
      }

      return filtered.map((restaurant) => ({
        ...restaurant,
        users_count: restaurant.users?.length || 0,
      }));
    },

    mobileHeaders() {
      return [
        { text: this.$t("title"), value: "name", cols: 6 },
        { text: this.$t("city"), value: "city.name", cols: 6 },
        { text: this.$t("address"), value: "address", cols: 6 },
        { text: "", value: "actions", cols: 12 },
      ];
    },
  },

  watch: {
    showEditDialog(val) {
      val || this.closeEditDialog();
    },
  },

  async created() {
    this.isLoading = true;
    try {
      const { data } = this.isDeliveryUser
        ? await restaurantService.getRestaurantsByUserId(this.currentUser.id)
        : await restaurantService.getAllRestaurants();
      this.allRestaurants = data;
    } finally {
      this.isLoading = false;
    }
  },

  methods: {
    getRestaurantContext(id) {
      for (let i = 0; i < this.allRestaurants.length; i++) {
        const restaurant = this.allRestaurants[i];

        if (restaurant.id === id) {
          return {
            restaurant,
            parentIndex: i,
            childIndex: -1,
            isChild: false,
          };
        }

        if (restaurant.children) {
          const childIndex = restaurant.children.findIndex((c) => c.id === id);
          if (childIndex !== -1) {
            return {
              restaurant: restaurant.children[childIndex],
              parentIndex: i,
              childIndex,
              isChild: true,
            };
          }
        }
      }
      return null;
    },

    filterUserAccessibleRestaurants(data) {
      if (this.isAdmin) return data;

      const userRestaurant = this.currentUser.restaurants?.[0];
      if (!userRestaurant) return [];

      if (
        this.currentUserRole?.pivot?.add_restoran === 1 &&
        this.currentUserRole?.type === "restaurant"
      ) {
        return userRestaurant.parent_id === 0
          ? data.filter((r) => r.id === userRestaurant.id)
          : this.currentUser.restaurants;
      }

      return data.filter((r) => r.id === userRestaurant.id);
    },

    openLocationDialog(item) {
      this.showLocationDialog = true;
      this.selectedRestaurantForLocation = item;
    },

    openLinkDialog(item) {
      this.linkRestaurant = item;
      this.linkDialog = true;
    },

    openServicesDialog(item) {
      this.selectedRestaurantForServices = item;
      this.showServicesDialog = true;
    },

    openContractsDialog(item) {
      this.selectedRestaurantForContracts = item;
      this.showContractsDialog = true;
    },

    async updateParentMenu(item) {
      try {
        await restaurantService.addParentMenu(item);
        this.showSuccessSnackbar(this.$t("parent_menu_updated_successfully"));
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_updating_parent_menu"));
      }
    },

    async updateMealSettings(item) {
      try {
        await restaurantService.setCanSetMeal(item);
        this.showSuccessSnackbar(this.$t("meal_settings_updated_successfully"));
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_updating_meal_settings"));
      }
    },

    async editRestaurant(item) {
      this.editedRestaurantId = item.id;
      this.prepareRestaurantForEdit(item);
      this.showEditDialog = true;
    },

    prepareRestaurantForEdit(item) {
      const editedRestaurant = { ...item };
      editedRestaurant.country = editedRestaurant.city?.country || [];

      editedRestaurant.restoran_tip_ugovora = editedRestaurant.restoran_tip_ugovora
        ? {
            ...editedRestaurant.restoran_tip_ugovora,
            paying_type: parseFloat(editedRestaurant.restoran_tip_ugovora.paying_type),
          }
        : { paying_type: "", amount: "" };

      this.editedRestaurant = editedRestaurant;
    },

    deleteRestaurant(item) {
      this.editedRestaurantId = item.id;
      this.editedRestaurant = { ...item };

      this.$refs.confirmBox
        .open(this.$t("delete_confirmation"), this.$t("confirm_delete_restaurant"), {
          color: "error",
        })
        .then(() => this.confirmDeleteRestaurant());
    },

    async confirmDeleteRestaurant() {
      try {
        await restaurantService.deleteRestaurant(this.editedRestaurant.id);

        const context = this.getRestaurantContext(this.editedRestaurant.id);
        if (!context) return;

        if (context.isChild) {
          this.allRestaurants[context.parentIndex].children.splice(context.childIndex, 1);
        } else {
          this.allRestaurants.splice(context.parentIndex, 1);
        }

        this.resetEditState();
        this.showSuccessSnackbar(this.$t("restaurant_deleted_successfully"));
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_deleting_restaurant"));
      }
    },

    closeEditDialog() {
      this.showEditDialog = false;
      this.$nextTick(() => this.resetEditState());
    },

    resetEditState() {
      this.editedRestaurant = { ...defaultRestaurant };
      this.editedRestaurantId = null;
    },

    async saveRestaurant(questionnaires = []) {
      this.isSaving = true;
      const payload = this.prepareRestaurantPayload();

      try {
        if (this.editedRestaurantId) {
          await this.updateExistingRestaurant(payload, questionnaires);
        } else {
          await this.createNewRestaurant(payload, questionnaires);
        }
      } finally {
        this.isSaving = false;
        this.closeEditDialog();
      }
    },

    prepareRestaurantPayload() {
      return {
        ...this.editedRestaurant,
        parent_id: this.editedRestaurant.parent?.id || 0,
        city: this.editedRestaurant.city.id,
        city_id: this.editedRestaurant.city.id,
        country: this.editedRestaurant.country.id,
      };
    },

    async updateExistingRestaurant(payload, questionnaires) {
      try {
        await restaurantService.updateRestaurant(payload.id, payload);

        const context = this.getRestaurantContext(payload.id);
        if (context) {
          Object.assign(context.restaurant, payload);
        }

        if (this.isAdmin) {
          await restaurantService.saveRestaurantQuestionnaires(
            payload.id,
            questionnaires
          );
        }

        this.showSuccessSnackbar(this.$t("restaurant_updated_successfully"));
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_updating_restaurant"));
        throw error;
      }
    },

    async createNewRestaurant(payload, questionnaires) {
      try {
        const { data } = await restaurantService.createRestaurant(payload);
        payload.id = data.id;

        if (payload.parent_id) {
          const parentIndex = this.allRestaurants.findIndex(
            (i) => i.id === payload.parent_id
          );
          this.allRestaurants[parentIndex].children.push(payload);
        } else {
          this.allRestaurants.push(payload);
        }

        if (this.isAdmin && questionnaires && questionnaires.length > 0) {
          await restaurantService.saveRestaurantQuestionnaires(
            payload.id,
            questionnaires
          );
        }

        this.showSuccessSnackbar(this.$t("restaurant_created_successfully"));
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_creating_restaurant"));
        throw error;
      }
    },
  },
};
</script>

<style scoped>
.status-filter-toggle {
  border: 1px solid rgba(0, 0, 0, 1);
  border-radius: 8px !important;
  overflow: hidden;
}

.status-filter-toggle .v-btn {
  text-transform: none;
}

.fixed-bottom-button {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
}
</style>
